import { FC, ReactElement, useState } from 'react';
import AppModal from '../app-modal/AppModal';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import i18next from 'i18next';
import { BlobResult, downloadBlob, useAuthFetchFromRestAPI } from '../../util/api';
import { PatientExportRequest } from '../../../../shared/requests/patientExport.request';

type ExportFormat = PatientExportRequest['format'];

type PatientExportModalProps = {
  show: boolean;
  patientId: string;
  onClose: () => void;
}

const PatientExportModal: FC<PatientExportModalProps> = ({
  show,
  patientId,
  onClose
}: PatientExportModalProps): ReactElement => {
  const [exportingFormat, setExportingFormat] = useState<ExportFormat | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { authFetchBlob } = useAuthFetchFromRestAPI();

  const handleExport = async (format: ExportFormat): Promise<void> => {
    setErrorMessage(null);
    setExportingFormat(format);

    try {
      const request: PatientExportRequest = { patientId, format };
      const result: BlobResult = await authFetchBlob('/api/v1/file-export/patient', { method: 'POST', body: request });

      downloadBlob(result.blob, result.fileName);
      onClose();
    } catch (error: any) {
      console.error(error);
      const msg: string = error?.message ?? (typeof error === 'string' ? error : i18next.t('generic.unknownError'));
      setErrorMessage(msg);
    } finally {
      setExportingFormat(null);
    }
  };

  return (
    <AppModal show={show} onHide={onClose} backdrop="static" keyboard>
      <Modal.Header closeButton>
        <Modal.Title>{i18next.t('patientExportModal.title')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {errorMessage && (
          <Alert variant="danger" className="mb-3">{errorMessage}</Alert>
        )}

        <p className="mb-0">
          {i18next.t('patientExportModal.description')}
        </p>

      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          type="button"
          onClick={onClose}
          disabled={!!exportingFormat}
        >
          {i18next.t('generic.cancel')}
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={() => handleExport('xlsx')}
          disabled={!!exportingFormat}
        >
          {exportingFormat === 'xlsx' && (
            <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
          )}

          {i18next.t('patientExportModal.excel')}
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={() => handleExport('pdf')}
          disabled={!!exportingFormat}
        >
          {exportingFormat === 'pdf' && (
            <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
          )}

          {i18next.t('patientExportModal.pdf')}
        </Button>
      </Modal.Footer>
    </AppModal>
  );
};

export default PatientExportModal;
